import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/InputDefault';
import { IconProvider } from '@/components/ui';
import { Card } from '@/components/ui/card';
import { DialogVariable } from '@/lib/stores/dialogStore';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/DefaultSelectDropdown';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { getTypeItemsForGame, resolveTypeElement, getInitialValue, getVariableType } from '@/lib/utils/typeStoreResolver';
import { TypeElement } from '@/lib/stores/typesStore';
import { TextAreaElement } from '../wizard-elements';

interface DialogVariableDeclarationsProps {
  variables: DialogVariable[];
  onUpdateVariables: (variables: DialogVariable[]) => void;
}

export function DialogVariableDeclarations({
  variables,
  onUpdateVariables
}: DialogVariableDeclarationsProps) {
  const { selectedGame } = useSimplifiedUserData();
  const [isAdding, setIsAdding] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [draftVariable, setDraftVariable] = useState<DialogVariable>({
    name: '',
    type: 'number',
    initialValue: 0,
    description: ''
  });
  const [draftSource, setDraftSource] = useState<'local' | 'typestore' | 'range-selector'>('typestore');
  const [hasManuallyInteracted, setHasManuallyInteracted] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);

  // Use ref to track latest variables without causing callback recreation
  const variablesRef = useRef(variables);
  useEffect(() => {
    variablesRef.current = variables;
  }, [variables]);

  // Get available TypeItems for the current game
  const availableTypes = useMemo(() => {
    if (!selectedGame?.id) return [];
    return getTypeItemsForGame(selectedGame.id);
  }, [selectedGame?.id]);

  const handleSaveDraft = useCallback(() => {
    if (!draftVariable.name.trim()) return;

    const currentVariables = variablesRef.current;

    if (isAdding) {
      const newVariables = [...currentVariables, draftVariable];
      onUpdateVariables(newVariables);
    } else if (editingIndex !== null) {
      const updated = [...currentVariables];
      updated[editingIndex] = draftVariable;
      onUpdateVariables(updated);
    }

    setIsAdding(false);
    setEditingIndex(null);
    setDraftVariable({
      name: '',
      type: 'number',
      initialValue: 0,
      description: ''
    });
  }, [draftVariable, isAdding, editingIndex, onUpdateVariables]);

  // Handle click outside to save if name is filled AND user has manually interacted
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (formRef.current && !formRef.current.contains(event.target as Node)) {
        // Only auto-save if user has manually typed/interacted AND name is filled
        if ((isAdding || editingIndex !== null) && draftVariable.name.trim() && hasManuallyInteracted) {
          handleSaveDraft();
        }
      }
    };

    if (isAdding || editingIndex !== null) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isAdding, editingIndex, draftVariable, hasManuallyInteracted, handleSaveDraft]);

  const startAdding = useCallback(() => {
    setIsAdding(true);
    setEditingIndex(null);
    setHasManuallyInteracted(false);
    setDraftVariable({
      name: '',
      type: 'number',
      initialValue: 0,
      description: ''
    });
    setDraftSource('local');
  }, []);

  const startEditing = useCallback((index: number) => {
    setEditingIndex(index);
    setIsAdding(false);
    setHasManuallyInteracted(false);
    setDraftVariable({ ...variables[index] });

    // Determine source based on variable configuration
    if (variables[index].type === 'range-selector') {
      setDraftSource('range-selector');
    } else if (variables[index].typeStoreRef) {
      setDraftSource('typestore');
    } else {
      setDraftSource('local');
    }
  }, [variables]);

  const handleCancelDraft = useCallback(() => {
    setIsAdding(false);
    setEditingIndex(null);
    setDraftVariable({
      name: '',
      type: 'number',
      initialValue: 0,
      description: ''
    });
  }, []);

  const removeVariable = (index: number) => {
    onUpdateVariables(variables.filter((_, i) => i !== index));
  };

  const handleSourceChange = (source: 'local' | 'typestore' | 'range-selector') => {
    setDraftSource(source);

    if (source === 'local') {
      setDraftVariable({
        ...draftVariable,
        typeStoreRef: undefined,
        type: 'number',
        initialValue: 0
      });
    } else if (source === 'range-selector') {
      // Range selector uses a number value that's checked against range brackets
      setDraftVariable({
        ...draftVariable,
        type: 'range-selector',
        initialValue: 0,
        typeStoreRef: undefined
      });
    }
  };

  const handleTypeItemChange = (typeId: string) => {
    // For range-selector, we only need typeId (no specific element)
    if (draftSource === 'range-selector') {
      const typeItem = availableTypes.find(t => t.id === typeId);
      setDraftVariable({
        ...draftVariable,
        typeStoreRef: { typeId },
        name: draftVariable.name || typeItem?.name.replace(/[^a-zA-Z0-9_]/g, '_') || ''
      });
    } else {
      // For regular typestore, user must select an element
      setDraftVariable({
        ...draftVariable,
        typeStoreRef: { typeId, elementId: '' }
      });
    }
  };

  const handleTypeElementChange = (typeId: string, elementId: string) => {
    const resolved = resolveTypeElement(typeId, elementId);
    if (!resolved) return;

    const { element, typeItem } = resolved;
    const varType = getVariableType(element, typeItem);
    const initialVal = getInitialValue(element);

    // Only auto-fill name if it's empty, don't override user's input
    const updatedName = draftVariable.name.trim()
      ? draftVariable.name
      : element.name.replace(/[^a-zA-Z0-9_]/g, '_');

    setDraftVariable({
      ...draftVariable,
      typeStoreRef: { typeId, elementId },
      type: varType,
      initialValue: initialVal,
      name: updatedName,
      rangeMin: element.rangeMin,
      rangeMax: element.rangeMax,
      increment: element.increment,
    });
  };

  const getValueDisplay = (variable: DialogVariable): string => {
    if (variable.type === 'boolean') {
      return variable.initialValue ? 'true' : 'false';
    }
    if (variable.type === 'range') {
      return `${variable.initialValue} (${variable.rangeMin ?? 0}-${variable.rangeMax ?? 100})`;
    }
    if (variable.type === 'range-selector' && variable.typeStoreRef?.typeId) {
      const typeItem = availableTypes.find(t => t.id === variable.typeStoreRef?.typeId);
      return `${variable.initialValue} (${typeItem?.name || 'ranges'})`;
    }
    return String(variable.initialValue);
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'boolean': return 'bg-blue-100 text-blue-700';
      case 'number': return 'bg-green-100 text-green-700';
      case 'range': return 'bg-purple-100 text-purple-700';
      case 'range-selector': return 'bg-indigo-100 text-indigo-700';
      case 'text': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const selectedTypeItem = draftVariable.typeStoreRef ? availableTypes.find(t => t.id === draftVariable.typeStoreRef?.typeId) : null;

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <IconProvider iconstring="fa-flask" size={16} />
          <h3 className="text-sm font-semibold">Dialog Variables</h3>
          <span className="text-xs text-muted-foreground">
            ({variables.length})
          </span>
        </div>
        {!isAdding && editingIndex === null && (
          <Button onClick={startAdding} size="sm" variant="outline">
            <IconProvider iconstring="fa-plus" className="mr-2" size={12} />
            Add Variable
          </Button>
        )}
      </div>

      {/* Add/Edit Form */}
      {(isAdding || editingIndex !== null) && (
        <div ref={formRef} className="p-3 bg-background rounded border border-blue-200 space-y-3 mb-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-foreground">
              {isAdding ? 'Add New Variable' : 'Edit Variable'}
            </span>
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  setHasManuallyInteracted(true);
                  handleSaveDraft();
                }}
                size="sm"
                variant="default"
                disabled={
                  !draftVariable.name.trim() ||
                  (draftSource === 'typestore' && !draftVariable.typeStoreRef?.elementId) ||
                  (draftSource === 'range-selector' && !draftVariable.typeStoreRef?.typeId)
                }
                className="h-7"
              >
                <IconProvider iconstring="fa-check" size={12} className="mr-1" />
                Save
              </Button>
              <Button
                onClick={handleCancelDraft}
                size="sm"
                variant="ghost"
                className="h-7"
              >
                <IconProvider iconstring="fa-times" size={12} className="mr-1" />
                Cancel
              </Button>
            </div>
          </div>

          {/* Variable Name */}
          <div>
            <label className="text-xs text-gray-600 mb-1 block">Name</label>
            <Input
              placeholder="variable_name"
              value={draftVariable.name}
              onChange={(e) => {
                const value = e.target.value.replace(/[^a-zA-Z0-9_]/g, '');
                setDraftVariable({ ...draftVariable, name: value });
                setHasManuallyInteracted(true);
              }}
              className="font-mono h-8 text-sm w-full"
              autoFocus
            />
          </div>

          {/* Source Selector */}
          <div>
            <label className="text-xs text-gray-600 mb-1 block">Source</label>
            <Select
              value={draftSource}
              onValueChange={(value: 'local' | 'typestore' | 'range-selector') => handleSourceChange(value)}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="local">Local Variable</SelectItem>
                <SelectItem value="typestore">Global Variable (Single)</SelectItem>
                <SelectItem value="range-selector">Global Variable (Range)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Range Selector Selection */}
          {draftSource === 'range-selector' ? (
            <div className="space-y-3">
              {/* TypeItem Selector */}
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Range TypeItem</label>
                <Select
                  value={draftVariable.typeStoreRef?.typeId || ''}
                  onValueChange={handleTypeItemChange}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Select range type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTypes
                      .filter(type => type.data?.some(el => el.valueType === 'range'))
                      .map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Show available ranges */}
              {draftVariable.typeStoreRef?.typeId && (() => {
                const typeItem = availableTypes.find(t => t.id === draftVariable.typeStoreRef?.typeId);
                const ranges = typeItem?.data?.filter(el => el.valueType === 'range') || [];
                return ranges.length > 0 ? (
                  <div className="bg-purple-50 p-2 rounded border border-purple-200">
                    <div className="text-xs font-semibold text-purple-900 mb-1">Available Ranges:</div>
                    <div className="space-y-1">
                      {ranges.map(range => (
                        <div key={range.id} className="text-xs text-purple-700">
                          • <span className="font-mono">{range.name}</span>: {range.rangeMin ?? 0} - {range.rangeMax ?? 100}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null;
              })()}
            </div>
          ) : draftSource === 'typestore' ? (
            <div className="space-y-3">
              {/* TypeItem Selector */}
              <div>
                <label className="text-xs text-gray-600 mb-1 block">TypeItem</label>
                <Select
                  value={draftVariable.typeStoreRef?.typeId || ''}
                  onValueChange={handleTypeItemChange}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Select type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* TypeElement Selector */}
              {selectedTypeItem && selectedTypeItem.data && selectedTypeItem.data.length > 0 && (
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Element</label>
                  <Select
                    value={draftVariable.typeStoreRef?.elementId || ''}
                    onValueChange={(elementId) => {
                      if (draftVariable.typeStoreRef) {
                        handleTypeElementChange(draftVariable.typeStoreRef.typeId, elementId);
                      }
                    }}
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="Select element..." />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedTypeItem.data.map((element: TypeElement) => (
                        <SelectItem key={element.id} value={element.id}>
                          {element.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          ) : (
            /* Local Configuration */
            <div className="space-y-3">
              {/* Type Selector */}
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Type</label>
                <Select
                  value={draftVariable.type}
                  onValueChange={(type: 'boolean' | 'number' | 'text' | 'range') => {
                    const updates: Partial<DialogVariable> = { type };
                    if (type === 'boolean') {
                      updates.initialValue = false;
                    } else if (type === 'number') {
                      updates.initialValue = 0;
                    } else if (type === 'text') {
                      updates.initialValue = '';
                    } else if (type === 'range') {
                      updates.initialValue = 0;
                      updates.rangeMin = 0;
                      updates.rangeMax = 100;
                      updates.increment = 1;
                    }
                    setDraftVariable({ ...draftVariable, ...updates });
                  }}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="boolean">Boolean</SelectItem>
                    <SelectItem value="number">Number</SelectItem>
                    <SelectItem value="text">Text</SelectItem>
                    <SelectItem value="range">Range</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Initial Value - only show for local variables */}
              {draftSource === 'local' && (
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Initial Value</label>
                  {draftVariable.type === 'boolean' ? (
                    <Select
                      value={String(draftVariable.initialValue)}
                      onValueChange={(value) => {
                        setDraftVariable({ ...draftVariable, initialValue: value === 'true' });
                      }}
                    >
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="true">true</SelectItem>
                        <SelectItem value="false">false</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : draftVariable.type === 'number' || draftVariable.type === 'range' ? (
                    <Input
                      type="number"
                      placeholder="0"
                      value={String(draftVariable.initialValue)}
                      onChange={(e) => {
                        setDraftVariable({
                          ...draftVariable,
                          initialValue: parseFloat(e.target.value) || 0
                        });
                      }}
                      className="h-8 text-sm"
                    />
                  ) : (
                    <Input
                      type="text"
                      placeholder="text value"
                      value={String(draftVariable.initialValue)}
                      onChange={(e) => {
                        setDraftVariable({ ...draftVariable, initialValue: e.target.value });
                      }}
                      className="h-8 text-sm"
                    />
                  )}
                </div>
              )}

              {/* Range Configuration */}
              {draftVariable.type === 'range' && (
                <>
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">Min</label>
                    <Input
                      type="number"
                      value={String(draftVariable.rangeMin ?? 0)}
                      onChange={(e) => {
                        setDraftVariable({ ...draftVariable, rangeMin: parseFloat(e.target.value) || 0 });
                      }}
                      className="h-8 text-sm w-full"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">Max</label>
                    <Input
                      type="number"
                      value={String(draftVariable.rangeMax ?? 100)}
                      onChange={(e) => {
                        setDraftVariable({ ...draftVariable, rangeMax: parseFloat(e.target.value) || 100 });
                      }}
                      className="h-8 text-sm w-full"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">Step</label>
                    <Input
                      type="number"
                      value={String(draftVariable.increment ?? 1)}
                      onChange={(e) => {
                        setDraftVariable({ ...draftVariable, increment: parseFloat(e.target.value) || 1 });
                      }}
                      className="h-8 text-sm w-full"
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {/* Description */}
          <div>
            <label className="text-xs text-gray-600 mb-1 block">Description (optional)</label>
            <TextAreaElement
              placeholder="Describe what this variable is used for..."
              value={draftVariable.description || ''}
              onChange={(e) => {
                setDraftVariable({ ...draftVariable, description: e });
              }}
              className="w-full"
              rows={2}
            />
          </div>
        </div>
      )}

      {/* Variables List */}
      {variables.length === 0 && !isAdding ? (
        <div className="text-center py-6 text-sm text-muted-foreground">
          <p>No variables declared yet.</p>
          <p className="text-xs mt-1">Click "Add Variable" to declare variables for this dialog.</p>
        </div>
      ) : (
        <div className="space-y-2">
          <TooltipProvider>
            {variables.map((variable, index) => (
              editingIndex === index ? null : (
                <div
                  key={index}
                  className="flex items-center gap-3 p-2 bg-white rounded border hover:border-gray-400 transition-colors"
                >
                  {/* Variable Name */}
                  <span className="font-mono text-sm font-semibold text-gray-900 min-w-[150px]">
                    {variable.name}
                  </span>
                  {/*
                  <span className={`text-xs px-2 py-1 rounded font-medium ${getTypeColor(variable.type)}`}>
                    {variable.type}
                  </span>

                  <span className="text-sm text-gray-600 flex-1">
                    {getValueDisplay(variable)}
                  </span>

                  {variable.typeStoreRef && variable.type !== 'range-selector' && (
                    <span className="text-xs px-2 py-1 rounded bg-orange-100 text-orange-700 flex items-center gap-1">
                      <IconProvider iconstring="fa-link" size={10} />
                      Global
                    </span>
                  )}
                  */}

                  {/* Range Selector Badge */}
                  {variable.type === 'range-selector' && variable.typeStoreRef && (
                    <span className="text-xs px-2 py-1 rounded bg-indigo-100 text-indigo-700 flex items-center gap-1">
                      <IconProvider iconstring="fa-layer-group" size={10} />
                      Ranges
                    </span>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-1 ml-auto">
                    {/* Documentation Icon with Tooltip */}
                    {variable.description && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            className="h-8 w-8 flex items-center justify-center rounded hover:bg-gray-100 text-gray-600"
                            type="button"
                          >
                            <IconProvider iconstring="fa-circle-info" size={14} />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-xs text-sm">{variable.description}</p>
                        </TooltipContent>
                      </Tooltip>
                    )}

                    {/* Edit Button */}
                    <button
                      onClick={() => startEditing(index)}
                      className="h-8 w-8 flex items-center justify-center rounded hover:bg-blue-100 text-blue-600"
                      type="button"
                      title="Edit variable"
                    >
                      <IconProvider iconstring="fa-pen-to-square" size={14} />
                    </button>

                    {/* Delete Button */}
                    <button
                      onClick={() => removeVariable(index)}
                      className="h-8 w-8 flex items-center justify-center rounded hover:bg-red-100 text-red-600"
                      type="button"
                      title="Delete variable"
                    >
                      <IconProvider iconstring="fa-trash" size={14} />
                    </button>
                  </div>
                </div>
              )
            ))}
          </TooltipProvider>
        </div>
      )}

      {variables.length > 0 && !isAdding && editingIndex === null && (
        <div className="mt-3 text-xs text-muted-foreground bg-blue-50 p-2 rounded">
          <IconProvider iconstring="fa-info-circle" size={10} className="inline mr-1" />
          Variables declared here will be available in dropdowns when setting values or checking conditions.
        </div>
      )}
    </Card>
  );
}
